import * as React from "react";
import { isValid } from "date-fns";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { Datepicker } from "../datepicker/datepicker.mjs";
import { usePropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { TextField } from "../textfield/textfield.mjs";
import { Tooltip } from "../../tooltip/tooltip.mjs";
import { useFormat, isEqualValue, isDateRange, getDate } from "../datepicker/datepicker-helpers.mjs";
const DateField = (props) => {
  const { format } = useFormat();
  usePropsChecker({
    componentProps: props,
    componentName: "DateField",
    defaultValueName: "defaultValue",
    valueName: "value",
    onChangeNames: "onChange",
    isComponentDisabled: props.isDisabled
  });
  usePropsChecker({
    componentProps: props,
    componentName: "DateField",
    defaultValueName: "defaultIsOpen",
    valueName: "isOpen",
    onChangeNames: "onToggle"
  });
  return /* @__PURE__ */ React.createElement(
    AnalyticsTrigger,
    {
      trigger: {
        callback: "onToggle",
        id: ([isOpen]) => "Date - ".concat(isOpen ? "Open" : "Close", " - ").concat(props.id),
        elementId: props.id,
        elementType: "date",
        interactionType: ([isOpen]) => isOpen ? "open" : "close"
      }
    },
    /* @__PURE__ */ React.createElement(DateFieldInner, { ...props, formatFn: format })
  );
};
const _DateFieldInner = class _DateFieldInner extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onValueChange = (value) => {
      if (!isEqualValue(this.value, value)) {
        if (!this.isValueControlled()) {
          this.setState({ value });
        }
        if (this.props.onChange) {
          this.props.onChange(value);
        }
      }
    };
    this.onIsOpenChange = (isOpen2) => {
      if (isOpen2 !== this.isOpen) {
        if (!this.isOpenControlled()) {
          this.setState({ isOpen: isOpen2 });
        }
        if (this.props.onToggle) {
          this.props.onToggle(isOpen2);
        }
      }
    };
    this.onSubmit = (value) => {
      var _a;
      this.onIsOpenChange(false);
      if ((_a = this.props.datepicker) == null ? void 0 : _a.onSubmit) {
        this.props.datepicker.onSubmit(value);
      }
    };
    this.onClear = () => {
      var _a;
      this.onValueChange(null);
      this.onIsOpenChange(false);
      if ((_a = this.props.datepicker) == null ? void 0 : _a.onClear) {
        this.props.datepicker.onClear();
      }
    };
    this.handleKeyDown = (event) => {
      switch (event.key) {
        case "Enter":
        case " ":
        case "ArrowDown":
          this.onIsOpenChange(true);
      }
    };
    const datePickerValue = props.defaultValue ? props.defaultValue : null;
    const isOpen = props.defaultIsOpen ? props.defaultIsOpen : false;
    this.state = {
      isOpen,
      value: datePickerValue
    };
  }
  isValueControlled(value = this.props.value) {
    return !!this.props.onChange && typeof value !== "undefined";
  }
  get value() {
    return this.isValueControlled(this.props.value) ? this.props.value : this.state.value;
  }
  isOpenControlled(isOpen = this.props.isOpen) {
    return !!this.props.onToggle && typeof isOpen !== "undefined";
  }
  get isOpen() {
    return this.isOpenControlled(this.props.isOpen) ? this.props.isOpen : this.state.isOpen;
  }
  getFieldValue() {
    var _a;
    const value = this.value;
    const { format } = this.props;
    if (!value) {
      return "";
    }
    const valueArray = [];
    if (isDateRange(value)) {
      valueArray.push(this.formatValue(value.from, format));
      if (value.to) {
        valueArray.push(this.formatValue(value.to, (_a = this.props.formatTo) != null ? _a : format));
      }
    } else {
      valueArray.push(this.formatValue(value, format));
    }
    return valueArray.join(" - ");
  }
  formatValue(date, format) {
    const value = getDate(date, !!this.props.times);
    if (isValid(value)) {
      return this.props.formatFn(value, format);
    }
    return "";
  }
  render() {
    return /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        modal: true,
        fixedWidth: false,
        isShown: this.isOpen,
        noPadding: true,
        onToggle: this.onIsOpenChange,
        placement: "bottom",
        trigger: "click",
        isCloseButtonVisible: false,
        "aria-labelledby": "".concat(this.props.id, "-label ").concat(this.props.id),
        referenceElement: /* @__PURE__ */ React.createElement(
          TextField,
          {
            className: "datefield",
            inputClass: "datefield__input",
            id: this.props.id,
            isFocused: this.isOpen,
            onFocus: () => null,
            onBlur: () => null,
            onKeyDown: this.handleKeyDown,
            isReadOnly: true,
            label: this.props.label,
            value: capitalizeFirstLetter(this.getFieldValue()),
            size: this.props.size,
            isDisabled: this.props.isDisabled,
            contentRenderer: this.props.contentRenderer,
            icon: "#calendar",
            input: {
              role: "combobox"
            },
            helpMessage: this.props.helpMessage,
            errorMessage: this.props.errorMessage,
            successMessage: this.props.successMessage,
            employeeMessage: this.props.employeeMessage,
            isInvalid: this.props.isInvalid,
            employee: this.props.employee,
            hideLabel: this.props.hideLabel,
            required: this.props.required
          }
        )
      },
      /* @__PURE__ */ React.createElement(
        Datepicker,
        {
          ...this.props.datepicker,
          onClear: this.onClear,
          onSubmit: this.onSubmit,
          onChange: this.onValueChange,
          value: this.value,
          times: this.props.times
        }
      )
    );
  }
};
_DateFieldInner.defaultProps = {
  format: "dd.MM.y"
};
let DateFieldInner = _DateFieldInner;
function capitalizeFirstLetter(str) {
  return str && str[0].toUpperCase() + str.slice(1);
}
export {
  DateField
};
